<template>
	<div class="p-grid">
		<div class="p-col-12">
			<div class="card">

        <Toolbar class="p-mb-4 page-toolbar">
          <template #start>
            <div class="p-d-flex p-ai-center p-flex-wrap">
              <div style="font-size: 1.4em; color: inherit" class="p-mr-3">{{ $t('Cashbox transactions') }}</div>
              <AddNewButton :label="$t('Add')" @click="addNewItem" v-if="cashboxesLength"/>
            </div>
          </template>
          <template #end v-if="cashboxesLength">
            <DatesRangeCalendar :minDate="computedMinDate"
                                ref="datesRangeCalendar"
                                :disabled="dataTableIsLoading"
                                @change-dates-range="changeDatesRange"
                                class="p-mr-2"/>
            <SearchInput :disabled="dataTableIsLoading"
                         @change-processed-search-value="changeProcessedSearchValue"
                         v-model="searchData"
                         @show-spinner="isLoading = true"/>
          </template>
        </Toolbar>
        <div class="p-d-flex p-jc-start p-flex-wrap p-mb-4 p-mt-0" v-if="cashboxesLength">
          <div v-for="(cashbox, index) of cashboxes"
               :key="index"
               @click="changeCashbox(cashbox.id)"
               @mouseenter="hoveredCashboxId = cashbox.id"
               @mouseleave="hoveredCashboxId = null"
               class="cashbox-block p-mr-2"
               :class="{
                'transparented-primary-color':
                  cashbox.id === cashboxId || cashbox.id === hoveredCashboxId
               }">
            {{ cashbox.name }}:
            <b>{{ formatCurrency(cashbox.total) }}</b>
          </div>
        </div>

        <Spinner v-if="isLoading"/>
        <DesktopDataTable v-else-if="!isLoading"
                          :dataTableIsLoading="dataTableIsLoading"
                          :showHeader="false"
                          :tableName="$t('Cashbox transactions')"
                          :itemsArrayIsEmpty="itemsArrayIsEmpty"
                          :sort="sort"
                          :showSearchInput="false"
                          :actionButtonsCount="actionButtonsCount"
                          @sort-items="sortItems"
                          :headers="[
              {name: 'type', title: $t('Type'), sortable: false},
              {name: 'order_id', title: $t('Order'), sortable: false},
              {name: 'invoice_id', title: $t('Invoice'), sortable: false},
              {name: 'date', title: $t('Date'), sortable: false},
              {name: 'cashbox_id', title: $t('Cashbox'), sortable: false},
              {name: 'description', title: $t('Description'), sortable: false},
              {name: 'sum', title: $t('Sum'), sortable: false},
              {name: 'total', title: $t('cashboxBalanceLabel'), sortable: false},
              {name: 'created_at', title: $t('Created'), sortable: false},
          ]">
          <template v-slot:body>
            <tr v-for="(item, index) of items"
                :key="index"
                :class="{'updated-item': isItemUpdated(item.id)}">
              <td>
                <span>{{ CashboxTransactionTypes?.find(t => t.type === item.type)?.label[$i18n.locale] }}</span>
              </td>
              <td>
                <router-link v-if="item.order && item.order.number && item.order.uid"
                             :to="{ path: `/orders/${item.order.uid}` }"
                             :target="settings.openAllLinksInSameTab ? '_self' : '_blank'"
                             rel="noopener"
                             draggable="false">
                  <span>{{ item.order.number }}</span>
                </router-link>
                <i v-else class="ti-minus"/>
              </td>
              <td>
                <template v-if="item.sales_invoice?.number">
                  <span v-if="!$store.getters.isAccountantOrHigher">{{ item.sales_invoice.number }}</span>
                  <router-link v-else-if="item.sales_invoice.number && item.sales_invoice.uid"
                               :to="{ path: '/sales-invoices', query: { show: item.sales_invoice.uid } }"
                               :target="settings.openAllLinksInSameTab ? '_self' : '_blank'"
                               rel="noopener"
                               draggable="false">
                    <span>{{ item.sales_invoice.number }}</span>
                  </router-link>
                  <i v-else class="ti-minus"/>
                </template>
                <template v-else-if="item.prepayment_invoice?.number">
                  <span v-if="!$store.getters.isAccountantOrHigher">{{ item.prepayment_invoice.number }}</span>
                  <router-link v-else-if="item.prepayment_invoice.number && item.prepayment_invoice.uid"
                               :to="{ path: '/prepayment-invoices', query: { show: item.prepayment_invoice.uid } }"
                               :target="settings.openAllLinksInSameTab ? '_self' : '_blank'"
                               rel="noopener"
                               draggable="false">
                    <span>{{ item.prepayment_invoice.number }}</span>
                  </router-link>
                  <i v-else class="ti-minus"/>
                </template>
                <i v-else class="ti-minus"/>
              </td>
              <td>
                {{ formatDate(item.date) }}
              </td>
              <td>
                <span v-if="item.cashbox">{{ item.cashbox.name }}</span>
              </td>
              <td style="max-width: 350px;">
                <div>
                  <span v-if="item.description">{{ item.description }}</span>
                  <i v-else class="ti-minus"/>
                </div>
                <div v-if="item.deleted_at" class="table-small-text p-text-secondary">
                  {{$t('Deleted')}}: {{ formatBy(item.deleted_at, item.deletedBy) }}
                </div>
              </td>
              <td>
                <span :class="item.sum >= 0 ? 'green-color' : 'warning-color'"><span v-if="item.sum >= 0">+</span>{{ formatCurrency(item.sum) }}</span>
              </td>
              <td>
                <span>{{ formatCurrency(item.total) }}</span>
              </td>

              <td class="table-date-line">
                <span v-if="item.created_at">
                  {{ formatBy(item.created_at, item.creator) }}
                </span>
                <i v-else class="ti-minus"/>
              </td>

              <td v-if="actionButtonsCount">
                <div class="actions" v-show="$store.getters.isAccountantOrHigher">
<!--                  <EditButton @click="editItem(item)"-->
<!--                              :disabled="!$store.getters.isAccountantOrHigher || item.type === 1 || item.deleted_at || dataTableIsLoading"/>-->
<!--                  <DeleteButton @click="confirmItemDelete(item)"-->
<!--                                :disabled="!$store.getters.isAccountantOrHigher || item.type === 1 || item.deleted_at || dataTableIsLoading"/>-->

                  <EditButton @click="editItem(item)"
                              v-if="$store.getters.isAccountantOrHigher && Number(item.type) !== 1 && !item.deleted_at"
                              :disabled="dataTableIsLoading"/>
                  <DeleteButton @click="confirmItemDelete(item)"
                                v-if="$store.getters.isAccountantOrHigher && Number(item.type) !== 1 && !item.deleted_at"
                                :disabled="dataTableIsLoading"/>
                </div>
              </td>
            </tr>
          </template>
        </DesktopDataTable>

        <OwnPaginator v-show="!itemsArrayIsEmpty"
                      :rows="pagination.perPage"
                      :currentPage="pagination.currentPage - 1"
                      :totalPages="pagination.pageCount"
                      :totalRecords="pagination.totalCount"
                      :rowsPerPageOptions="[5,10,25,50,100,200]"
                      :pageLinkSize="11">
        </OwnPaginator>

        <TotalRecordsData :pagination="pagination" :itemsArrayIsEmpty="itemsArrayIsEmpty"/>

        <CashboxTransactionModal :visible="itemModal"
                                 :item="item"
                                 :selectedCashboxId="selectedCashbox?.id"
                                 :cashboxes="cashboxes"
                                 @update-items="updateItems"
                                 @close="closeItemModal"/>

        <ConfirmDeleteModal :visible="confirmItemDeleteModal"
                            :modalDataIsLoading="modalDataIsLoading"
                            @close="closeConfirmItemDeleteModal"
                            @delete-item="deleteItem"/>
			</div>

		</div>
	</div>
</template>

<script>
import httpClient from '@/services/http.services'
import CashboxTransactionTypes from '@/translations/CashboxTransactionTypes'
import CashboxTransactionModal from '@/pages/finance/components/CashboxTransactionModal'
import formatMixins from '@/mixins/formatMixins'
import datatableMixins from "@/mixins/datatableMixins";
import DatesRangeCalendar from "@/components/DatesRangeCalendar";
import settings from "@/settings";
import showErrorsMixins from "@/mixins/showErrorsMixins";

export default {
  mixins: [ formatMixins, datatableMixins, showErrorsMixins ],
  components: { CashboxTransactionModal, DatesRangeCalendar },
	data() {
		return {
      hoveredCashboxId: null,
      cashboxes: [],
      selectedCashbox: null,
      cashboxId: null,
      isLoading: true,
      fieldsExpand: '' +
          'creator,' +
          'cashbox,' +
          'order,' +
          'sales_invoice,' +
          'prepayment_invoice,' +
          'deletedBy,' +
          'car,' +
          'employee',
      apiLink: `cashbox-transaction`,
      routePath: '/cashbox-transactions',
      CashboxTransactionTypes,
		}
	},
  mounted() {
    this.scrollToTop()
    if (settings.autoLogoutTimeout && this.$store.state.logoutTimestampIsOverdue) return false
    this.generateThisMonthRange()
    this.checkRouteQuery()
    this.getPageData()
	},
  methods: {
    getPageData() {
      if (!this.userCanGetData || this.fetchInFlight) return
      this.getCashoxesAndItems()
    },
    getCashoxesAndItems() {
      if (!this.userCanGetData || this.fetchInFlight) return
        this.getUserCashboxes().then(() => {
          if (this.cashboxId) {
            this.getItems()
          } else {
            this.isLoading = false
          }
        })
    },
    async getUserCashboxes() {
      if (!this.userCanGetData) return
      try {
        const { status, data } = await httpClient('cashbox/get-user-cashboxes')
        if (status === 200 && data) {
          this.cashboxes = Array.isArray(data) ? data : []
          this.$store.commit('updateUserCashboxes', this.cashboxes)

          if (!this.cashboxes?.length) return

          if (!this.selectedCashbox) {
            this.selectedCashbox = this.cashboxes[0]
            this.cashboxId = this.selectedCashbox.id
          } else {
            this.selectedCashbox = this.cashboxes.find(i => i.id === this.selectedCashbox.id)
          }
        } else {
          this.showUnknownError()
        }
      } catch(err) {
        this.showError(err)
      }
    },
    changeCashbox(id) {
      if (id === this.cashboxId) return false
      if (id || id === 0) {
        this.selectedCashbox = this.cashboxes.find(cashbox => cashbox.id === id)
        this.cashboxId = this.selectedCashbox.id
      } else {
        this.cashboxId = this.selectedCashbox.id
      }

      this.searchData = null
      this.clearLastSearching()
      this.resetRouteAndPagination()

      this.getItems(true)
    },
    updateItems(itemId, clearItem = false) {
      if (itemId) {
        this.updatedItemId = itemId
      }
      if (clearItem) {
        this.item = {}
        this.submitted = false
      }

      this.getCashoxesAndItems()
    },
    async getItems(showSpinner = false) {
      if (!this.userCanGetData || this.fetchInFlight) return
      this.$store.commit('SET_FETCH_IN_FLIGHT', true)
      if (showSpinner) this.isLoading = true
      this.dataTableIsLoading = true
      const ctrl = this.setController?.('cashboxTransactions')
      try {
        const { status, data, headers } = await httpClient({
          url: this.apiLink,
          method: 'post',
          params: {
            page: this.pagination.currentPage,
            'per-page': this.pagination.perPage,
            sort: this.sort.direction + this.sort.column,
            search: this.processedSearchValue ?? null,
            expand: this.fieldsExpand,
          },
          data: {
            cashbox_id: this.cashboxId,
            // userId: this.$route.params.id,
            dateFrom: this.fromTimestamp,
            dateTo: this.toTimestamp,
          },
          signal: ctrl?.signal
        })
        if (status === 200 && data && Array.isArray(data)) {
          const hasPaginationHeaders = !!headers['x-pagination-total-count']
          if (hasPaginationHeaders) {
            this.pagination.totalCount = +headers['x-pagination-total-count']
            this.pagination.pageCount  = +headers['x-pagination-page-count']
          } else {
            this.pagination.totalCount = data.length
            this.pagination.pageCount  = 1
          }

          this.items = data

          if (!data.length) {
            this.pagination.pageCount = 1
            this.pagination.currentPage = 1
          }
        } else {
          this.items = []
          this.showUnknownError()
        }
      } catch(err) {
        this.showError(err)
      } finally {
        this.$store.commit('SET_FETCH_IN_FLIGHT', false)
        this.isLoading = false
        this.dataTableIsLoading = false
        if (this._abortors?.cashboxTransactions === ctrl) {
          this._abortors.cashboxTransactions = null
        }
      }
    },
    async deleteItem() {
      if (this.itemToDelete.payment_id || this.itemToDelete.sales_invoice_id || this.itemToDelete.deleted_at) {
        return false
      }
      this.modalDataIsLoading = true
      try {
        const { status, data } = await httpClient.post('cashbox-transaction/delete', { id: this.itemToDelete.id })
        if (status === 200 && data?.success) {
          this.$toast.add({severity:'success', detail: this.$t('Data deleted'), life: settings.toastLife})
          this.updateItems(this.itemToDelete.id)
          this.closeConfirmItemDeleteModal()
        } else {
          this.showUnknownError()
        }
      } catch(err) {
        this.showError(err)
      } finally {
        this.modalDataIsLoading = false
      }
    },
	},
  computed: {
    computedMinDate() {
      if (this.$store.getters.isAccountantOrHigher) {
        return null
      } else {
        let d = new Date()
        const month = d.getMonth() - 1
        const year = d.getFullYear()
        d = new Date(year, month, 1)
        return d
      }
    },
    cashboxesLength() {
      return this.cashboxes?.length ?? 0
    },
    actionButtonsCount() {
      if (this.$store.getters.isAccountantOrHigher) {
        return 2
      } else {
        return 0
      }
    },
  }
}
</script>

<style scoped lang="scss">
td {
  height: 44px
}

.cashbox-block {
  border-radius: 3px;
  padding: 12px 14px;
  cursor: pointer;
}
</style>